import {addYears, eachMonthOfInterval, format, getYear, isSameMonth, subYears} from "date-fns";
import groupBy from "lodash/groupBy";
import type React from "react";
import {memo, useCallback, useEffect, useMemo, useState} from "react";

import type {OptionType} from "../Select";
import SelectGeneric from "../SelectGeneric";

type Props = {
    currentDate: Date;
    onSelectMonth: (date: Date) => void;
};

const SelectMonths: React.FC<Props> = ({currentDate, onSelectMonth}) => {
    const [selectedValue, setSelectedValue] = useState<string>();

    const monthOptions = useMemo(() => {
        const listMonths = groupBy(
            eachMonthOfInterval({
                start: subYears(new Date(), 4),
                end: addYears(new Date(), 2)
            }),
            date => getYear(date)
        );
        // Sort years in ascending order (oldest first)
        const sortedYears = Object.keys(listMonths).sort((a, b) => Number(a) - Number(b));

        return sortedYears.map(year => {
            return {
                label: year,
                options: listMonths[year]
                    .sort((a, b) => a.getTime() - b.getTime()) // Sort months in ascending order
                    .map(month => ({
                        value: month.toISOString(),
                        label: format(month, "MMM yyyy")
                    }))
            };
        });
    }, []);

    const handleChange = useCallback(
        (value: string) => {
            setSelectedValue(value);
            onSelectMonth(new Date(value));
        },
        [onSelectMonth]
    );

    useEffect(() => {
        const selectedOption = monthOptions.reduce<OptionType<string> | undefined>((acc, group) => {
            const option = group.options.find(option => {
                return isSameMonth(new Date(option.value), currentDate);
            });
            if (option) {
                return option;
            }
            return acc;
        }, undefined);
        setSelectedValue(selectedOption?.value);
    }, [monthOptions, currentDate]);

    return (
        <SelectGeneric<string>
            isClearable={false}
            isSearchable={false}
            options={monthOptions}
            placeholder="Month"
            testID="current-month-year"
            value={selectedValue}
            onChange={handleChange}
        />
    );
};

export default memo(SelectMonths);
